// Details on one logbook entry. ID comes from a parameter in the URL or data can be
// passed in state from prior page.
//
// If in URL, loads from database and puts in location.state.
// In state I have:
//    state.task
//    state.item
//    state.back : optional [Extras] for ItemBreadcrumbs
//
// An entry filed under a trip name gets a fourth crumb linking to that trip's
// page, the way a named trip leg links back to its group in Trip.jsx. It is
// derived from the entry itself, not handed over in state, so a deep link or a
// reload shows it too.
//
// Every field is directly editable on the page (no popup) -- and autosaves:
// typed edits persist ~1.5s after the last keystroke or on leaving the
// field, discrete controls persist immediately. There is no Save button; the
// global SaveStatusPill reports progress and errors. Delete lives below the
// field list. Navigating away only prompts when an edit is held invalid or a
// save failed (see useUnsavedChangesGuard).
//
// Types that carry a route (Captain's / Pilot's -- see supportsRoute) also get
// the waypoint list and the map, exactly as Trip.jsx does, and for the same
// reason: From and To ARE the route's Start and End, so they get no inputs of
// their own and the field list is split around the waypoint list. The route
// lives at `fields.geoRoute` inside the entry's blob (TripRoute.mjs owns that
// contract; the key differs from a trip's because the Pilot's registry already
// owns `fields.route` as free text) and is tracked by a generation counter, so
// a save completing mid-drag can't swallow newer route edits.
//
// The map here is titled "Route" and carries no weather: an entry records a
// voyage that already happened.
import { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { useStatusMessage, Attachments, Confirm, DataCard, Field, CardHeader, Spinner } from '../../components';
import * as Database from '../../utilities/Database.mjs';
import * as Urls from '../../fleet-shared/Urls.mjs';
import {
  fieldsForType,
  entryHeading,
  flattenEntry,
  splitEntryFields,
  deriveEntryFields,
  parseEntryFields,
  supportsRoute,
  LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';
import { tripFieldForType, tripNameOf } from '../../fleet-shared/LogbookTrips.mjs';
import { KEY_ENTRY, tripKeyFor } from '../../fleet-shared/TripIdentity.mjs';
import { tripSuggestionsFromEntries, withTripSuggestions } from '../../utilities/tripSuggestions';
import { withStartCounterSuggestion } from '../../utilities/counterSuggestion';
import { syncItemCounterFromEntry, withUpdatedCounter } from '../../utilities/itemCounterSync.mjs';
import { usePreviousEndCounter } from './usePreviousEndCounter';
import {
  ENTRY_ROUTE_KEY,
  routeFromEntry,
  mergeRouteIntoFields,
  backfillEndpointNames,
  resolveRouteAgainstPlaces,
  anchorName,
  entryWithAnchorNames,
} from '../../fleet-shared/TripRoute.mjs';
import { loadPlaceIndex } from '../../utilities/places.mjs';
import TripWaypoints from '../trip/TripWaypoints';
import { useRouteLocator } from '../trip/useRouteLocator';
// TODO: Get rid of useSharedItemPageState
import { useSharedItemPageState, pageLogbookEntry, pageLogbook, pageItem } from '../item/useSharedItemPageState';
import { useUnsavedChangesGuard } from '../../utilities/useUnsavedChangesGuard';
import { useAutosave } from '../../utilities/useAutosave';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { entryLane } from '../../utilities/saveLanes';
import { validateRequiredName } from '../../utilities/Validators.mjs';

// Lazy: TripRouteMap is the only import path that pulls in maplibre-gl
// (~250 KB gz); keep it out of the entry bundle.
const TripRouteMap = lazyWithReload(() => import('../../components/map/TripRouteMap'), 'TripRouteMap');

// From and To are the route's Start and End. Unlike a trip, they live INSIDE
// the entry's `fields` blob rather than in columns -- everything else about
// the mirroring is the same.
const ENDPOINT_KEYS = ['from', 'to'];

// The route as this page edits it: anchors that were never named fall back to
// the entry's From/To so the Start/End rows aren't blank beside a value the
// entry does have. Those come out of the blob here, not off the row.
function routeDraftFor(entry) {
  const json = parseEntryFields(entry);
  return backfillEndpointNames(routeFromEntry(entry), { from: json.from, to: json.to });
}

// Build this type's field list for editing, cloning each field so we never
// mutate the shared registry, and inject the "Counter Based On" select
// options (Maintenance only) from the item's counters at runtime. Defensive
// about a missing `item.itemCounter` the same way EditLogbookEntry.jsx is.
function buildEntryFields(typeId, item) {
  const fields = fieldsForType(typeId).map((f) => ({ ...f }));

  for (let i = 0; i < fields.length; i++) {
    if (fields[i].key === 'itemCounterId') {
      const counterItems = item?.itemCounter?.items ?? [];
      fields[i].selects = counterItems.map((counterItem, index) => ({
        // index + 1 to make room for no-counter, unshifted below.
        id: counterItem.id,
        index: index + 1,
        name: counterItem.name,
      }));
      fields[i].selects.unshift({ id: null, index: 0, name: '(None)' });
      break;
    }
  }

  return fields;
}

const LogbookEntry = () => {
    // Passed on URL:
    const { itemId, logbookId, entryId } = useParams();
    const location = useLocation();
    // For the map card's place popup only. A plain navigate rather than the
    // shared-page-state handover: /me/places must refetch.
    const navigate = useNavigate();
    // Stable, so the map card's PlacesLayer memo holds: a fresh arrow every
    // render would re-render the layer on every render of this page, and it
    // re-stringifies the whole gazetteer when it does.
    const handleEditPlace = useCallback(
      (placeId) => navigate(Urls.urlForPlace(placeId)),
      [navigate]
    );
    const handlePlaceVisits = useCallback(
      (placeId) => navigate(Urls.urlForPlaceVisits(placeId)),
      [navigate]
    );

    // Status message
    const [StatusMessage, {showText, showError, showLoading, hideStatus }] = useStatusMessage();

    // Confirm delete
    const [showConfirm, setShowConfirm] = useState(false);

    // Page data/state
    const [{ item, setItem, logbook, Breadcrumbs, completedTask, setCompletedTask, navigateTo }] = useSharedItemPageState({
        page: pageLogbookEntry, 
        itemId: itemId,
        logbookId: logbookId,
        pageItemId: entryId,
        showLoading: showLoading, 
        hideLoading: hideStatus, 
        showError: showError
    });

    const typeId = logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;
    const hasRoute = supportsRoute(typeId);

    // The freshest item for the counter sync below: onSaved is captured when a
    // save is SCHEDULED, so reading `item` there directly would see the copy
    // from that render.
    const itemRef = useRef(null);
    itemRef.current = item;

    // This logbook's existing trips, for the Trip Name box's menu. Read lazily
    // -- the input asks once, and only when the user actually goes near that
    // field -- because this is the whole-logbook read, and most visits here are
    // to edit an hours figure or a note.
    const loadTripSuggestions = useCallback(
      () => Database.listCompletedTasksForLogbook(logbookId)
        .then((entries) => tripSuggestionsFromEntries(entries, typeId)),
      [logbookId, typeId]
    );
    // The previous entry's End Counter, offered on the Start Counter row. Null
    // on a type with no counters, on the logbook's first entry, and when the
    // entry before this one left its End Counter blank -- in each of which the
    // injector below hands the fields back untouched.
    const startCounterHint = usePreviousEndCounter({
      logbookId: logbookId,
      entry: completedTask,
      typeId: typeId,
    });
    const entryFields = withStartCounterSuggestion(
      withTripSuggestions(
        item ? buildEntryFields(typeId, item) : [],
        loadTripSuggestions
      ),
      startCounterHint
    );
    const recordKey = entryLane(entryId);

    // Map route edits. The draft is what the map and waypoint list render;
    // saves pick it up via the refs below (payloads are built when the save
    // RUNS, so they always carry the newest draft).
    const [routeDraft, setRouteDraft] = useState(null);
    const routeDraftRef = useRef(null);
    const routeGen = useRef(0); // bumped on every route edit
    const persistedRouteGen = useRef(0); // the generation the server has
    const lastBuiltRouteGen = useRef(0); // the generation the in-flight payload carries

    // The latest server copy of the entry, for the payload's identity keys and
    // the `fields` blob merge -- a ref so a queued save that runs between
    // renders still sees the newest copy.
    const entryRef = useRef(null);
    const loadedEntryId = useRef(undefined);

    // The shared gazetteer, as { byId, byKey }. A place row OWNS the position
    // of every waypoint bound to it; the lat/lon in the blob is the last-known
    // snapshot that keeps the map drawable when this never arrives.
    //
    // In a ref for the resolve and the locator's synchronous curated check, and
    // ALSO in state, because the map card now draws every row as a layer and
    // the dots have to appear when the index lands. The SAME read, set in one
    // `.then` -- not a second usePlaceIndex() beside it, which could straddle
    // the module cache's expiry and spell one place two ways on one page.
    const placeIndexRef = useRef(null);
    const [placeIndex, setPlaceIndex] = useState(null);

    // Resolve the draft against the gazetteer once the index lands.
    //
    // Seeding only: if the user has already touched the route this session
    // (routeGen > 0) the index is a snapshot from BEFORE their edit, and a drag
    // writes back to the row fire-and-forget -- so resolving now would snap the
    // point they just moved back to where the row was when the page opened.
    useEffect(() => {
      if (!hasRoute) return undefined;
      let mounted = true;
      loadPlaceIndex().then((index) => {
        if (!mounted) return;
        placeIndexRef.current = index;
        // Before the seeding guard below: the layer draws whether or not the
        // resolve has anything to say.
        setPlaceIndex(index);
        if (routeGen.current !== 0) return;
        const base = routeDraftRef.current;
        const resolved = resolveRouteAgainstPlaces(base, index);
        if (resolved === base) return;
        setRouteDraft(resolved);
        routeDraftRef.current = resolved;
        routeGen.current = 1;
      });
      return () => { mounted = false; };
    }, [hasRoute]);

    // A new (or different) entry loaded -- (re)seed the route draft. The
    // autosave hook below reseeds the form itself, keyed on the same change.
    //
    // Resolved against the gazetteer when the index has already arrived;
    // otherwise the effect above does it when it does. A resolve that MOVED
    // something starts the route one generation ahead of the server, which is
    // the whole lazy-write-back mechanism: NO SAVE IS SCHEDULED HERE (that
    // takes a setFormState, which this deliberately doesn't call -- opening a
    // page must never be a mutation), but the next save the user causes for any
    // reason carries the correction with it.
    if (completedTask && completedTask.id !== loadedEntryId.current) {
      loadedEntryId.current = completedTask.id;
      entryRef.current = completedTask;
      if (hasRoute) {
        const base = routeDraftFor(completedTask);
        const draft = placeIndexRef.current
          ? resolveRouteAgainstPlaces(base, placeIndexRef.current)
          : base;
        setRouteDraft(draft);
        routeDraftRef.current = draft;
        // resolveRouteAgainstPlaces returns the SAME object when it had nothing
        // to say, which is what tells a real correction from a no-op here.
        routeGen.current = draft === base ? 0 : 1;
      }
      persistedRouteGen.current = 0;
      lastBuiltRouteGen.current = 0;
    }

    // Autosave: the form edits the flattened entry (json-backed fields
    // hoisted to the top level); buildPayload splits it back and re-attaches
    // the identity keys, same as the old Save path. deriveEntryFields keeps
    // Start/End Counter/Hours linked on every edit. The server response
    // (not the form payload) feeds page state so any server-normalized
    // fields (e.g. updatedAt) make it back.
    const {
      formState,
      setFormState: updateFormState,
      fieldErrors,
      flush,
      guardDirty,
    } = useAutosave({
      recordKey,
      original: completedTask ? flattenEntry(completedTask, typeId) : null,
      fields: entryFields,
      deriveState: (previous, next) => deriveEntryFields(previous, next, typeId),
      validate: entryFields.some((f) => f.key === 'name') ? validateRequiredName : undefined,
      buildPayload: (state) => {
        const current = entryRef.current || completedTask;
        // `preserve` keeps blob keys this registry doesn't know about -- the
        // route, above all. An AWSJSON write replaces the whole attribute, so
        // without it every save here would delete the entry's map.
        const payload = splitEntryFields(state, typeId, { preserve: current.fields });
        payload.id = current.id;
        payload.itemId = current.itemId;
        if (current.logbookId) {
          payload.logbookId = current.logbookId;
        }

        if (routeGen.current > persistedRouteGen.current) {
          const route = routeDraftRef.current;
          payload.fields = mergeRouteIntoFields(payload.fields, route, ENTRY_ROUTE_KEY);

          // handleRouteChange mirrors the From/To values off the route on every
          // EDIT, but a gazetteer resolve moves and RENAMES points without
          // going through it (deliberately -- that call is what would schedule
          // a save on page load). So the mirror is re-derived here, or a
          // resolved route would ship beside a From naming a place the
          // gazetteer has since renamed.
          //
          // Into `payload.fields`, not `payload`: unlike a trip's columns,
          // these live in the blob. Re-deriving can't fight a user edit,
          // because the ROUTE is the source of truth -- an anchor that exists
          // as a waypoint is only ever renamed through the waypoint, and
          // anchorName returns undefined for the case that isn't (an anchor
          // that isn't in the route), which this skips.
          //
          // There is no `distance` term, unlike Trip.jsx: an entry stores no
          // distance. RouteSummaryCard and the per-trip page's totals both
          // measure the route directly, so there is no second copy to drift.
          ENDPOINT_KEYS.forEach((kind) => {
            const name = anchorName(route, kind);
            if (name !== undefined) payload.fields[kind] = name;
          });
        }
        lastBuiltRouteGen.current = routeGen.current;
        return payload;
      },
      save: (payload) => Database.updateCompletedTask(payload),
      onSaved: (saved) => {
        if (!saved) return;
        // Only a save that carried the latest route generation marks the route
        // persisted -- newer drag edits stay pending.
        persistedRouteGen.current = Math.max(persistedRouteGen.current, lastBuiltRouteGen.current);
        entryRef.current = saved;
        setCompletedTask(saved);
        // The last entry of a Captain's Log moves the item's own counter (see
        // itemCounterSync.mjs for the guards). Fire-and-forget: a failed SYNC
        // must not fail the entry save that triggered it.
        syncItemCounterFromEntry({
          entry: saved,
          typeId,
          item: itemRef.current,
          onCounterSaved: (counter) => setItem((prev) => withUpdatedCounter(prev, counter)),
        }).catch((err) => console.error('Item counter sync failed', err));
      },
    });

    // Warn before navigating away (nav bar, breadcrumbs, etc.) only when an
    // edit is held invalid or a save failed -- valid pending edits flush on
    // the way out. See useUnsavedChangesGuard.js for the browser
    // back/forward limitation.
    const { promptOpen: showLeavePrompt, confirmLeave, cancelLeave, markClean } = useUnsavedChangesGuard(guardDirty);

    // Route edits from either editor -- the waypoint list above the map, or
    // the map itself -- land here and autosave like any field (debounced:
    // drags are rapid-fire).
    //
    // An anchor's name and the entry's From/To are one value in two places, so
    // a rename has to update both. That mirroring lives HERE rather than in
    // each editor: the waypoint list, the map's marker modal and a marker drag
    // all funnel through this one function. The setFormState call (made even
    // when no name changed) is also what schedules the save; buildPayload picks
    // the route up from the refs when the save runs.
    function handleRouteChange(nextRoute) {
      setRouteDraft(nextRoute);
      routeDraftRef.current = nextRoute;
      routeGen.current += 1;

      const renamed = {};
      ENDPOINT_KEYS.forEach((kind) => {
        const name = anchorName(nextRoute, kind);
        // `|| ''` so a null value and an unnamed anchor aren't seen as a
        // change -- that would dirty the form on an unrelated route edit.
        if (name !== undefined && name !== (formState[kind] || '')) {
          renamed[kind] = name;
        }
      });
      updateFormState({ ...formState, ...renamed });
    }

    // A Start/End row for an anchor that isn't on the map yet: there's no
    // waypoint to rename, so the value is edited on its own.
    function handleEndpointChange(kind, name) {
      updateFormState({ ...formState, [kind]: name });
    }

    // Geocoding and tap-to-place, owned here because BOTH editors offer them:
    // every row in the waypoint list has "Lookup place" / "Place on map", and the map
    // card has its empty state and half-placed prompt. One owner means one
    // queue of points waiting for a tap. It reads the route through the same
    // ref the save payloads do, so a geocode that lands mid-drag merges onto
    // the newest route rather than the one it started from.
    const locator = useRouteLocator({
      route: routeDraft,
      routeRef: routeDraftRef,
      onRouteChange: handleRouteChange,
      endpointName: (kind) => (formState ? formState[kind] : '') || '',
      placeIndexRef,
    });

    // Confirm delete, then delete item.
    function deleteTapped() {
      setShowConfirm(true);
    }

    // User confirmed they want to delete this item.
    async function onConfirmDelete() {
      try {
        // show deleting
        showText('Deleting...');
        setShowConfirm(false);

        // Cancel any pending or in-flight autosave first -- a late update
        // against the deleted row must neither error nor resurrect it.
        saveQueue.cancel(recordKey);

        // Delete the entry.
        await Database.deleteCompletedTask(completedTask.id);

        // Deleting discards any held edits too -- markClean() so the
        // navigation below isn't itself intercepted by the unsaved-changes
        // guard right after the user already confirmed the delete (it takes
        // effect synchronously; we navigate in this same tick below).
        markClean();

        // No need for status.
        hideStatus();

        // Return to the page the user came from. Logbook list sends a hint
        // (`fromLogbookList`); the item-page summary does not, so its absence
        // means we should land back on the item page.
        if (location.state?.fromLogbookList) {
          navigateTo(pageLogbook);
        } else {
          navigateTo(pageItem, itemId);
        }
      } catch (err) {
        setShowConfirm(false);
        showError(err);
      }
    }

    // TODO: Add email logbook entry
    // function emailTapped() {
    //   console.log('email tapped');
    // }

    // TODO: Links to itemId, itemCounterId, taskId?

    // The crumb back to this entry's trip page. `tripFieldForType` is the whole
    // "which logbooks have trips" rule (null on the Pilot's Logbook and on
    // Maintenance) -- don't test type ids here.
    //
    // Read from the FORM, not the saved entry, so the crumb tracks the Trip Name
    // field as it is edited -- appearing when a name is first typed and going
    // away when it is cleared, without waiting for the autosave debounce. The
    // saved entry is the fallback for the render before the form seeds.
    const tripField = tripFieldForType(typeId);
    const liveTripName = tripField && formState ? formState[tripField.key] : undefined;
    const tripName = String(
      (liveTripName !== undefined
        ? liveTripName
        : (completedTask ? tripNameOf(completedTask, typeId) : '')) ?? ''
    ).trim();
    // The KEY the crumb links to comes from the same live form state, so it
    // tracks a trip the user has just picked in the typeahead as readily as the
    // name does -- and so the crumb goes to THIS trip rather than to whichever
    // other one happens to be spelled the same.
    const liveTripId = tripField?.idKey && formState ? formState[tripField.idKey] : undefined;
    const tripKey = tripField
      ? tripKeyFor({
        name: tripName,
        tripGroupId: liveTripId !== undefined
          ? liveTripId
          : parseEntryFields(completedTask)[tripField.idKey],
        ownId: completedTask?.id,
        alonePrefix: KEY_ENTRY,
      })
      : null;
    const tripCrumbs = (tripField && tripName !== '')
      ? [{ name: tripName, url: Urls.urlForLogbookTrip(itemId, logbookId, tripKey) }]
      : [];

    const visibleFields = entryFields.filter((f) => !f.inlineWith);

    // On a route-carrying type, From and To are edited through the waypoint
    // list's Start/End rows, so they get no inputs of their own and the field
    // list is split around it -- head, [waypoints], tail.
    //
    // Split at the FIRST endpoint and filter the endpoints out of the tail
    // (rather than slicing between them) so a field sitting between From and
    // To -- neither type has one today -- can't be silently swallowed.
    const firstEndpoint = hasRoute
      ? visibleFields.findIndex((f) => ENDPOINT_KEYS.includes(f.key))
      : -1;
    const headFields = firstEndpoint < 0 ? visibleFields : visibleFields.slice(0, firstEndpoint);
    const tailFields = firstEndpoint < 0
      ? []
      : visibleFields.slice(firstEndpoint).filter((f) => !ENDPOINT_KEYS.includes(f.key));

    // `wide` puts the label in its own left column, sharing the waypoint
    // list's grid so the two read as one form. The inset layout's row-relative
    // widths can't line up against a list that runs the full card width.
    const fieldLayout = hasRoute ? 'wide' : 'inset';

    const renderField = (field, index) => (
      <Field
        key={field.key}
        field={field}
        companion={entryFields.find((f) => f.inlineWith === field.key) || null}
        state={formState}
        setState={updateFormState}
        setFocus={index === 0}
        error={fieldErrors[field.key]}
        layout={fieldLayout}
      />
    );

    return (
      <div>
        <Breadcrumbs extras={tripCrumbs} />
        <StatusMessage />

        {completedTask && formState && (
          <div>
            {/* entryHeading gets no placeIndex, so the heading carries the FULL
                names. This is the entry's own detail page -- room to be exact,
                and the boxes below edit those very strings. Lists shorten; a
                detail page does not.

                It reads a display copy, though: the From/To in the blob mirror
                the route as of the last save, so a place renamed in /me/places
                since then is reachable only through the waypoint's placeId --
                and `routeDraft` holds it, resolved, with the user's unsaved
                renames on top. Display only; `completedTask` and every payload
                built from it are untouched. */}
            <DataCard
              header=<CardHeader
                text={entryHeading(entryWithAnchorNames(completedTask, routeDraft), typeId)}
              />
              body=<div>
                {/* Leaving any field flushes the autosave debounce -- onBlur
                    bubbles up from the inputs to this container (it also
                    covers the waypoint list's inputs). */}
                <div onBlur={() => flush()}>
                  <div className="container">
                    {headFields.map(renderField)}
                  </div>

                  {hasRoute && (
                    <TripWaypoints
                      route={routeDraft}
                      endpoints={{ from: formState.from, to: formState.to }}
                      locator={locator}
                      onRouteChange={handleRouteChange}
                      onEndpointChange={handleEndpointChange}
                      // An entry records a voyage that already happened: the
                      // route arrived whole (TripToLogbook, or one drawing) and
                      // the vias are a record, not the work. Hours, the
                      // counters and Notes are what this page collects, and a
                      // long via list pushes them off the screen. A trip LEG is
                      // the opposite case -- Trip.jsx deliberately doesn't pass
                      // this.
                      collapsibleVias
                    />
                  )}

                  {tailFields.length > 0 && (
                    <div className="container">
                      {/* Never index 0 -- the head always renders first, so no
                          tail field steals initial focus. */}
                      {tailFields.map((field) => renderField(field, 1))}
                    </div>
                  )}
                </div>

                <div className="container m-2 d-flex justify-content-center align-items-center gap-2 flex-wrap">
                  <Button variant="danger" onClick={deleteTapped}>
                    Delete
                  </Button>
                </div>
              </div>
            />

            {/* No weather: this entry records a voyage that already happened,
                so a forecast for it is noise -- and it would still spend an
                NWS/NDBC request per waypoint. Edits flow back here so the
                page's autosave (and the unsaved-changes guard) owns saving. */}
            {hasRoute && (
              <Suspense fallback={<Spinner />}>
                <TripRouteMap
                  trip={formState}
                  route={routeDraft}
                  locator={locator}
                  onRouteChange={handleRouteChange}
                  placeIndex={placeIndex}
                  onEditPlace={handleEditPlace}
                  onPlaceVisits={handlePlaceVisits}
                  title="Route"
                  showWeather={false}
                />
              </Suspense>
            )}

            <Attachments bucket={Urls.urlForLogbookEntryStorage(itemId, completedTask.id)} />
          </div>
        )}

        <Confirm
            show={showConfirm}
            onConfirm={onConfirmDelete}
            onHide = {() => setShowConfirm(false)}
            body='Are you sure?'
            confirmText='Confirm Delete'
            title={`Delete`}>
        </Confirm>

        <Confirm
            show={showLeavePrompt}
            onConfirm={confirmLeave}
            onHide={cancelLeave}
            body="Some changes couldn't be saved. Leave and discard them?"
            cancelText='Stay'
            confirmText='Discard Changes'
            title='Unsaved Changes'>
        </Confirm>

        {locator.nearbyModal}
      </div>
    );
}

export default LogbookEntry;
